import type {
  TrainingSessionData,
  IMediaDevices,
  PublicSessionDemoBlock,
} from "@workspace/types";
import { inject, injectable } from "inversify";
import { makeAutoObservable, runInAction } from "mobx";
import { AvaturnHead } from "@avaturn-live/web-sdk";
import { isAxiosError } from "axios";
import { waitForAvatarSession } from "@workspace/core";
import {
  createPublicTrainingSession,
  completePublicTrainingSession,
  getPublicTrainingSession,
  getPublicDemoRecap,
} from "../api/training-sessions";
import { extractApiErrorCode } from "../api/error";
import {
  trackDemoEvent,
  trackSessionEvent,
  type SessionEventData,
  type SessionEventType,
} from "../analytics/events";
import { DEMO_DURATION_MS } from "../constants/demo";
import { mapWireDemoSignal } from "../utils/demo-signal";
import { parseDemoAppMessage } from "../utils/demo-app-message";
import { LandingServiceID } from "./symbols";
import type { IRemainingTime } from "@/lib/types/training";
import type {
  ILandingSessionManager,
  DemoCreateError,
  DemoCreateErrorState,
  DemoRecoveryState,
} from "@/lib/types/store";
import type { CoachTurn, ICoachManager } from "@/lib/types/coach";
import type {
  IDemoCallControlManager,
  IDemoDeviceCheckManager,
  IDemoClockManager,
  IDemoSignalsManager,
} from "@/lib/types/demo-session";

const DEMO_CREATE_ERROR_CODES: readonly DemoCreateError[] = [
  "demo_link_not_found",
  "demo_link_paused",
  "demo_link_session_cap_reached",
];

const AVATAR_CONNECT_TIMEOUT_MS = 20_000;
const COMPLETE_RETRY_DELAYS_MS = [1000, 2000, 4000];

class AvatarTimeoutError extends Error {
  constructor() {
    super("Avatar connection timed out.");
  }
}

function isDemoCreateError(code: string | null): code is DemoCreateError {
  return (
    code !== null &&
    (DEMO_CREATE_ERROR_CODES as readonly string[]).includes(code)
  );
}

function isRetryableCompleteError(error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === undefined) return true;
  return status >= 500 || status === 409 || status === 429;
}

function resolveAvaturnHost(): string {
  return (
    process.env.NEXT_PUBLIC_AVATURN_API_HOST ||
    (process.env.NEXT_PUBLIC_CONFIGURATION === "staging"
      ? "https://api.staging.avaturn.live"
      : "https://api.avaturn.live")
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Marcus training and the public demo share this store; demo pause/freeze/recovery lives in DemoCallControlStore. */
@injectable()
export class LandingSessionStore implements ILandingSessionManager {
  sessionId: string | null = null;
  state: "initial" | "ready" | "started" | "finished" | "media-error" =
    "initial";
  avatarMediaStream: MediaStream | null = null;
  userVideoStream: MediaStream | null = null;
  isAvatarSpeaking: boolean = false;
  coachTurns: CoachTurn[] = [];
  mediaPermissions: { audio: boolean; video: boolean } = {
    audio: true,
    video: true,
  };
  private mediaDevices: MediaDeviceInfo[] | null = null;
  selectedDevices = {
    audioinput: "default",
    audiooutput: "default",
    videoinput: "",
  };

  createError: DemoCreateErrorState | null = null;
  isDemo: boolean = false;
  demoInfo: PublicSessionDemoBlock | null = null;
  recoveryState: DemoRecoveryState = "idle";
  cameraDeniedMessage: string | null = null;

  private briefingData: TrainingSessionData["briefing"] | null = null;
  private liveSessionToken: string | null = null;
  private avatar: AvaturnHead | null = null;
  private demoMicStream: MediaStream | null = null;
  private watchedVideoTrack: MediaStreamTrack | null = null;
  /** Last actual mic/camera intent, insulated from hardware-forced flips to `mediaPermissions`. */
  private micIntendedOn = true;
  private cameraIntendedOn = true;
  private cameraGeneration = 0;
  private recoveryGeneration = 0;
  /** Bumped by dispose() so an in-flight startDemo() can tell it was torn down. */
  private startGeneration = 0;
  private avatarInitFailed: boolean = false;

  constructor(
    @inject(LandingServiceID.Timer) readonly timer: IRemainingTime,
    @inject(LandingServiceID.CoachStore) readonly coach: ICoachManager,
    @inject(LandingServiceID.DemoDeviceCheckStore)
    readonly deviceCheck: IDemoDeviceCheckManager,
    @inject(LandingServiceID.DemoClockStore)
    readonly demoClock: IDemoClockManager,
    @inject(LandingServiceID.DemoSignalsStore)
    readonly signals: IDemoSignalsManager,
    @inject(LandingServiceID.DemoCallControlStore)
    private readonly callControl: IDemoCallControlManager,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.callControl.bindHost({
      getDemoSessionId: () => (this.isDemo ? this.sessionId : null),
      isCallLive: () => this.state === "started",
      setDemoInfo: (demo) => {
        runInAction(() => {
          this.demoInfo = demo;
        });
      },
      setLocalAudio: (enabled) => {
        this.avatar?.toggleLocalAudio(enabled);
      },
      restoreLocalAudio: () => {
        if (this.micIntendedOn) this.avatar?.toggleLocalAudio(true);
      },
      cancelAvatarTasks: async () => {
        await this.avatar?.cancelAllTasks();
      },
      endCall: () => this.end(),
    });
  }

  get devices(): IMediaDevices | null {
    if (!this.mediaDevices) return null;

    return this.mediaDevices.reduce((acc, device) => {
      if (!acc[device.kind]) {
        acc[device.kind] = [];
      }
      acc[device.kind].push(device);
      return acc;
    }, {} as IMediaDevices);
  }

  get briefing(): TrainingSessionData["briefing"] | null {
    return this.briefingData;
  }

  async createSession(
    scenarioType: string,
    demoLinkSlug?: string,
    previewToken?: string,
  ): Promise<TrainingSessionData> {
    runInAction(() => {
      this.createError = null;
    });

    let data: TrainingSessionData;
    try {
      data = await createPublicTrainingSession({
        scenarioType,
        demoLinkSlug,
        previewToken,
      });
    } catch (error) {
      const errorCode = extractApiErrorCode(error);
      if (isDemoCreateError(errorCode)) {
        runInAction(() => {
          this.createError = { code: errorCode, slug: demoLinkSlug };
        });
      }
      throw error;
    }

    runInAction(() => {
      this.sessionId = data.id;
      this.briefingData = data.briefing;
      this.liveSessionToken = data.live_session_token;
      this.isDemo = !!data.demo;
      this.demoInfo = data.demo ?? null;
    });

    this.track("session_created", {
      sessionId: data.id,
      scenarioType,
    });

    return data;
  }

  clearCreateErrorForSlug(slug: string): void {
    if (this.createError?.slug !== slug) return;

    runInAction(() => {
      this.createError = null;
    });
  }

  /** Reload with no live store: resolve completed vs unrecoverable. */
  async checkRecovery(sessionId: string): Promise<void> {
    const gen = ++this.recoveryGeneration;
    runInAction(() => {
      this.recoveryState = "checking";
    });
    try {
      const data = await getPublicTrainingSession(sessionId);
      if (gen !== this.recoveryGeneration) return;
      runInAction(() => {
        this.recoveryState = data.completed_at ? "completed" : "unrecoverable";
      });
    } catch {
      if (gen !== this.recoveryGeneration) return;
      runInAction(() => {
        this.recoveryState = "unrecoverable";
      });
    }
  }

  /** Marcus only; the demo uses startDemo(). Idempotent. */
  async init({ trainingId }: { trainingId: string }): Promise<void> {
    if (this.sessionId && this.sessionId !== trainingId) {
      throw new Error(
        `Session ID mismatch: expected '${trainingId}', got '${this.sessionId}'.`,
      );
    }

    if (this.state === "media-error") {
      throw new Error(
        "Cannot initialize: previous media error. Call cleanupSession() first.",
      );
    }

    if (this.avatar) {
      return;
    }

    try {
      runInAction(() => {
        this.state = "initial";
        this.sessionId = trainingId;
      });

      await this.requestMediaDevices();

      if (!this.liveSessionToken) {
        throw new Error("Session token not found. Create session first.");
      }

      const avatar = this.createAvatar();
      avatar.init().catch((error) => {
        console.error("[LandingSessionStore] Avatar init failed:", error);
        runInAction(() => {
          this.avatarInitFailed = true;
        });
      });

      runInAction(() => {
        this.avatar = avatar;
        this.avatarMediaStream = avatar.mediaStream || null;
        this.state = "ready";
      });
    } catch (error) {
      console.error("[LandingSessionStore] Init failed:", error);
      runInAction(() => {
        this.state = "media-error";
      });
      throw error;
    }
  }

  /**
   * Demo-only entry point: the device check hands over its confirmed streams
   * and the call joins immediately. Failure returns to `initial` (device
   * check), never Marcus's media-error.
   */
  async startDemo(): Promise<void> {
    if (!this.isDemo) {
      throw new Error("startDemo() is only valid for a demo session.");
    }
    if (!this.sessionId || !this.liveSessionToken) {
      throw new Error("No demo session to start. Create it first.");
    }
    if (this.state === "started") {
      return;
    }

    const generation = this.startGeneration;
    const isStale = (): boolean =>
      generation !== this.startGeneration || this.state === "finished";
    this.signals.reset();
    this.callControl.reset();
    const { micId, speakerId, claimedMic } = this.claimDeviceCheckStreams();

    this.demoClock.reset();
    this.demoClock.setLocalFallback(DEMO_DURATION_MS);

    const avatarTimeout = this.createAvatarTimeoutGuard();
    avatarTimeout.promise.catch(() => {});
    let avatar: AvaturnHead | null = null;
    try {
      avatar = this.createAvatar({ immediatelyJoin: true });
      // dispose() aborts init's pending GET; unconsumed, that rejection would go unhandled.
      const initPromise = avatar.init();
      initPromise.catch(() => {});

      const created = avatar;
      runInAction(() => {
        this.avatar = created;
        this.avatarMediaStream = created.mediaStream || null;
        this.state = "ready";
      });

      try {
        // init() resolves on the first avatar video track, not on join.
        await Promise.race([initPromise, avatarTimeout.promise]);
        if (isStale()) return;
        await Promise.race([
          created.setInputDevices({ audioDeviceId: micId }),
          avatarTimeout.promise,
        ]);
        if (isStale()) return;

        if (speakerId && speakerId !== "default") {
          // Best-effort: no setSinkId (e.g. Safari) rejects and default output still works;
          // Promise.resolve().then() also catches a sync throw.
          await Promise.race([
            Promise.resolve().then(() => created.setOutputDevice({ outputDeviceId: speakerId })),
            avatarTimeout.promise,
          ]).catch(() => {});
          if (isStale()) return;
        }
      } catch (error) {
        if (!isStale()) {
          this.track("session_error", {
            sessionId: this.sessionId ?? undefined,
            reason:
              error instanceof AvatarTimeoutError
                ? "avatar_timeout"
                : "avatar_join_failed",
            errorMessage: error instanceof Error ? error.message : String(error),
          });
        }
        throw error;
      }

      this.onJoined();

      this.signals.watchConnection();
      await Promise.all([
        this.hydrateDemoSignals(isStale),
        this.callControl.syncClockFromServer(isStale),
      ]);
      if (isStale()) return;
      this.callControl.watchBoundary();
    } catch (error) {
      if (isStale()) return;

      runInAction(() => {
        this.avatar = null;
        this.state = "initial";
      });
      await avatar?.dispose().catch(() => {});
      this.demoClock.reset();
      this.returnDevicesToDeviceCheck();
      throw error;
    } finally {
      // Runs on every path so a retry never orphans the device-check mic track.
      claimedMic?.getTracks().forEach((track) => track.stop());
      runInAction(() => {
        if (this.demoMicStream === claimedMic) this.demoMicStream = null;
      });
      avatarTimeout.cancel();
    }
  }

  private claimDeviceCheckStreams(): {
    micId: string;
    speakerId: string;
    claimedMic: MediaStream | null;
  } {
    const { deviceCheck } = this;
    const { micStream, cameraStream } = deviceCheck.claimStreams();
    const micId = deviceCheck.selectedMicId;
    const speakerId = deviceCheck.selectedSpeakerId;
    const cameraId = deviceCheck.selectedCameraId;

    runInAction(() => {
      if (micStream) {
        this.demoMicStream = micStream;
        this.mediaPermissions.audio = true;
        this.micIntendedOn = true;
      }
      // A retry's claim may carry no camera — keep the existing one.
      if (cameraStream) {
        this.userVideoStream?.getTracks().forEach((track) => track.stop());
        this.userVideoStream = cameraStream;
      }
      this.mediaPermissions.video = !!this.userVideoStream;
      this.cameraIntendedOn = !!this.userVideoStream;
      this.selectedDevices.audioinput = micId;
      this.selectedDevices.videoinput = cameraId === "default" ? "" : cameraId;
      this.mediaDevices = deviceCheck.audioDevices;
      this.cameraDeniedMessage = deviceCheck.cameraDeniedMessage;
    });
    if (cameraStream) this.attachVideoTrackWatch(cameraStream);

    return { micId, speakerId, claimedMic: micStream };
  }

  /** The claimed mic is gone after a failed join, so restart the check and hand the camera back. */
  private returnDevicesToDeviceCheck(): void {
    const camera = this.userVideoStream;
    if (camera) {
      this.detachVideoTrackWatch();
      runInAction(() => {
        this.userVideoStream = null;
      });
      this.deviceCheck.adoptCameraStream(camera);
    }
    void this.deviceCheck.start();
  }

  private createAvatarTimeoutGuard(): { promise: Promise<never>; cancel: () => void } {
    let timeoutId: ReturnType<typeof setTimeout>;
    const promise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new AvatarTimeoutError());
      }, AVATAR_CONNECT_TIMEOUT_MS);
    });
    return {
      promise,
      cancel: () => clearTimeout(timeoutId),
    };
  }

  /** Demo uses immediatelyJoin so the SDK sequences GET /sessions → join
   * itself; a store-side join() raced init(). */
  private createAvatar(options: { immediatelyJoin?: boolean } = {}): AvaturnHead {
    const { immediatelyJoin = false } = options;
    const apiHost = resolveAvaturnHost();
    const avatar = new AvaturnHead({
      sessionToken: this.liveSessionToken!,
      immediatelyJoin,
      // join() isn't called on this path, so the mic starts off via config; onJoined() unmutes.
      ...(immediatelyJoin ? { startAudioOff: true } : {}),
      apiHost,
      audioSource: true,
    });

    this.wireAvatarEvents(avatar);
    return avatar;
  }

  private wireAvatarEvents(avatar: AvaturnHead): void {
    avatar.on("avatar_started_speaking", () => {
      runInAction(() => {
        this.isAvatarSpeaking = true;
      });
    });
    avatar.on("avatar_ended_speaking", () => {
      runInAction(() => {
        this.isAvatarSpeaking = false;
      });
    });
    // 'off' is ignored: it also fires for our own mute; only blocked/interrupted are hardware loss.
    avatar.on("local_mic_state_change", (state) => {
      if (state === "blocked" || state === "interrupted") {
        runInAction(() => {
          this.mediaPermissions.audio = false;
        });
      } else if (state === "playable") {
        runInAction(() => {
          this.mediaPermissions.audio = this.micIntendedOn;
        });
      }
    });
    avatar.on("event", ({ value }) => {
      if (!value?.data?.event) return;

      switch (value.data.event) {
        case "coach_mode_activated":
          this.coach.startSpeaking();
          break;
        case "coach_mode_deactivated":
          this.coach.stopSpeaking();
          break;
        case "coach_text":
          this.appendCoachText(value.data);
          break;
        case "demo_signal":
          this.handleDemoSignal(value.data);
          break;
        default: {
          const message = parseDemoAppMessage(value.data);
          if (message) this.callControl.handleAppMessage(message);
        }
      }
    });
  }

  private handleDemoSignal(data: unknown): void {
    const card = mapWireDemoSignal(data);
    if (card) this.signals.push(card);
  }

  /** Defensive hydrate for signals fired before the listener attached. */
  private async hydrateDemoSignals(isStale: () => boolean): Promise<void> {
    if (!this.sessionId) return;
    try {
      const recap = await getPublicDemoRecap(this.sessionId);
      if (isStale()) return;
      const cards = recap.signals
        .map(mapWireDemoSignal)
        .filter((card): card is NonNullable<typeof card> => card !== null);
      this.signals.hydrate(cards);
    } catch {
      // Best-effort only — the recap route retries this independently.
    }
  }

  // One box per turn: sentences arrive one message at a time and grow the box
  // opened by the turn's first sentence, which also fixes its timestamp.
  private appendCoachText(d: {
    segment_uid: string;
    text: string;
    elapsed_ms: number;
  }): void {
    // Sentences arrive without surrounding whitespace, so join them with a space.
    // The LLM writes markdown emphasis the TTS just reads over; drop the markers.
    const clean = d.text.replace(/\*/g, "").trim();
    if (!clean) return;

    const last = this.coachTurns.at(-1);
    if (last?.segmentUid === d.segment_uid) {
      last.text = `${last.text} ${clean}`;
      return;
    }

    this.coachTurns.push({
      segmentUid: d.segment_uid,
      text: clean,
      elapsedMs: d.elapsed_ms,
    });
  }

  async join(): Promise<void> {
    if (this.state !== "ready") {
      throw new Error(
        `Cannot join session: invalid state '${this.state}'. Expected 'ready'.`,
      );
    }

    if (!this.avatar) {
      throw new Error("Avatar not initialized. Call init() first.");
    }

    if (!this.sessionId) {
      throw new Error("No session ID. Create session first.");
    }

    // `avatar.init()` is started in init() but cannot be awaited there: its
    // promise settles only after the avatar's first video frame, which arrives
    // after join(). Wait for the session it fetches instead, so clicking the
    // moment the button unlocks doesn't hit "Session not initialized".
    await waitForAvatarSession(this.avatar, {
      isAborted: () => this.avatarInitFailed,
    });

    await this.avatar.join({ startAudioOff: true });

    this.onJoined();
  }

  private onJoined(): void {
    if (!this.sessionId) return;

    if (this.mediaPermissions.audio) {
      this.avatar?.toggleLocalAudio(true);
    }

    if (!this.isDemo) {
      this.timer.startTimer();
      this.timer.on("end", this.end);
    }

    runInAction(() => {
      this.state = "started";
    });

    this.track("session_started", {
      sessionId: this.sessionId,
    });
  }

  async end(): Promise<void> {
    if (this.state !== "started") {
      throw new Error(
        `Cannot end session: invalid state '${this.state}'. Expected 'started'.`,
      );
    }

    if (!this.sessionId) {
      throw new Error("No session ID to end.");
    }

    const sessionId = this.sessionId;
    const isDemo = this.isDemo;

    this.callControl.stop();
    this.signals.unwatchConnection();
    this.timer.off("end", this.end);
    this.timer.stopTimer();
    this.cameraGeneration++;
    if (isDemo) this.demoClock.reset();

    runInAction(() => {
      this.state = "finished";
    });

    this.track("session_ended", {
      sessionId,
    });

    this.stopMediaStreams();

    if (this.avatar) {
      void this.avatar.dispose().catch(() => {});
      this.avatar = null;
    }

    if (isDemo) {
      await this.completeDemoSession(sessionId);
    } else {
      await completePublicTrainingSession(sessionId);
    }
  }

  /** Retries with backoff; the recap would otherwise poll a never-completed session. */
  private async completeDemoSession(sessionId: string): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      try {
        await completePublicTrainingSession(sessionId);
        return;
      } catch (error) {
        const delayMs = isRetryableCompleteError(error)
          ? COMPLETE_RETRY_DELAYS_MS[attempt]
          : undefined;
        if (delayMs === undefined) {
          trackDemoEvent("session_error", {
            sessionId,
            errorMessage:
              error instanceof Error ? error.message : "Failed to complete the demo session",
          });
          return;
        }
        await wait(delayMs);
      }
    }
  }

  setMediaPermission(
    target: keyof ILandingSessionManager["mediaPermissions"],
    value: boolean,
  ): void {
    runInAction(() => {
      this.mediaPermissions[target] = value;
      if (target === "audio") this.micIntendedOn = value;
      if (target === "video") this.cameraIntendedOn = value;
    });

    if (this.sessionId) {
      this.track("media_permission_changed", {
        sessionId: this.sessionId,
        mediaType: target,
        enabled: value,
      });
    }

    if (!this.avatar) return;

    if (target === "audio") {
      this.avatar.toggleLocalAudio(value);
    }
  }

  async changeMediaDevice(
    type: "audiooutput" | "videoinput" | "audioinput",
    id: string,
  ): Promise<void> {
    runInAction(() => {
      this.selectedDevices[type] = id;
    });

    if (!this.avatar) return;

    if (type === "audiooutput") {
      await this.avatar.setOutputDevice({
        outputDeviceId: id,
      });
    } else {
      await this.avatar.setInputDevices({
        audioDeviceId: type === "audioinput" ? id : undefined,
        videoDeviceId: type === "videoinput" ? id : undefined,
      });
    }
  }

  /** Local self-view only; never published to the SDK. */
  async toggleDemoCamera(enabled: boolean): Promise<void> {
    const gen = ++this.cameraGeneration;

    if (!enabled) {
      this.detachVideoTrackWatch();
      this.userVideoStream?.getTracks().forEach((track) => track.stop());
      runInAction(() => {
        this.userVideoStream = null;
        this.mediaPermissions.video = false;
        this.cameraIntendedOn = false;
        this.cameraDeniedMessage = null;
      });
      return;
    }

    try {
      const stream = await this.openCameraStream();
      if (gen !== this.cameraGeneration) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.userVideoStream?.getTracks().forEach((track) => track.stop());
      runInAction(() => {
        this.userVideoStream = stream;
        this.mediaPermissions.video = true;
        this.cameraIntendedOn = true;
        this.cameraDeniedMessage = null;
      });
      this.attachVideoTrackWatch(stream);
    } catch {
      if (gen !== this.cameraGeneration) return;
      runInAction(() => {
        this.mediaPermissions.video = false;
        this.cameraIntendedOn = false;
        this.cameraDeniedMessage = "Camera is blocked — you can still stay on the call without it.";
      });
    }
  }

  private async openCameraStream(): Promise<MediaStream> {
    const base: MediaTrackConstraints = {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: "user",
    };
    const deviceId = this.selectedDevices.videoinput;
    if (!deviceId) {
      return navigator.mediaDevices.getUserMedia({ video: base });
    }

    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { ...base, deviceId: { exact: deviceId } },
      });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      if (name !== "NotFoundError" && name !== "OverconstrainedError") throw error;
      // The chosen camera is gone — fall back to the default one.
      runInAction(() => {
        this.selectedDevices.videoinput = "";
      });
      return navigator.mediaDevices.getUserMedia({ video: base });
    }
  }

  dispose(): void {
    this.startGeneration++;
    this.callControl.reset();
    this.stopMediaStreams();
    this.deviceCheck.dispose();
    this.demoClock.reset();
    this.signals.reset();

    if (this.avatar) {
      void this.avatar.dispose().catch(() => {});
    }

    this.timer.off("end", this.end);
    this.timer.dispose();
    this.cameraGeneration++;
    this.recoveryGeneration++;

    runInAction(() => {
      this.sessionId = null;
      this.briefingData = null;
      this.liveSessionToken = null;
      this.state = "initial";
      this.mediaDevices = null;
      this.avatar = null;
      this.avatarInitFailed = false;
      this.isAvatarSpeaking = false;
      this.coachTurns = [];
      this.createError = null;
      this.isDemo = false;
      this.demoInfo = null;
      this.recoveryState = "idle";
      this.mediaPermissions = { audio: true, video: true };
      this.selectedDevices = { audioinput: "default", audiooutput: "default", videoinput: "" };
      this.micIntendedOn = true;
      this.cameraIntendedOn = true;
      this.cameraDeniedMessage = null;
    });

    this.coach.reset();
  }

  private track(event: SessionEventType, data: SessionEventData): void {
    if (this.isDemo) trackDemoEvent(event, data);
    else trackSessionEvent(event, data);
  }

  private async requestMediaDevices(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: "user",
      },
      audio: true,
    });

    runInAction(() => {
      this.userVideoStream = stream;
    });
    this.attachVideoTrackWatch(stream);

    const devices = await navigator.mediaDevices.enumerateDevices();
    const filteredDevices = devices.filter((d) => !!d.deviceId);

    const hasAudioInput = filteredDevices.some((d) => d.kind === "audioinput");

    if (!hasAudioInput) {
      throw new Error("No audio input device found");
    }

    runInAction(() => {
      this.mediaDevices = filteredDevices;

      const videoDevice = filteredDevices.find((d) => d.kind === "videoinput");
      if (videoDevice) {
        this.selectedDevices.videoinput = videoDevice.deviceId;
      }
    });
  }

  /** Only the video track — Marcus's `userVideoStream` also carries the mic
   * track (see `requestMediaDevices`), so this must never touch `mediaPermissions.audio`. */
  private attachVideoTrackWatch(stream: MediaStream): void {
    this.detachVideoTrackWatch();
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    this.watchedVideoTrack = track;
    track.addEventListener("ended", this.handleVideoTrackEnded);
    track.addEventListener("mute", this.handleVideoTrackMuted);
    track.addEventListener("unmute", this.handleVideoTrackUnmuted);
  }

  private detachVideoTrackWatch(): void {
    if (!this.watchedVideoTrack) return;
    this.watchedVideoTrack.removeEventListener("ended", this.handleVideoTrackEnded);
    this.watchedVideoTrack.removeEventListener("mute", this.handleVideoTrackMuted);
    this.watchedVideoTrack.removeEventListener("unmute", this.handleVideoTrackUnmuted);
    this.watchedVideoTrack = null;
  }

  /** stop() never fires `ended`, so this only catches external loss. */
  private handleVideoTrackEnded(): void {
    runInAction(() => {
      this.mediaPermissions.video = false;
    });
  }

  /** OS-level block without ending the track; `unmute` recovers. */
  private handleVideoTrackMuted(): void {
    runInAction(() => {
      this.mediaPermissions.video = false;
    });
  }

  private handleVideoTrackUnmuted(): void {
    runInAction(() => {
      this.mediaPermissions.video = this.cameraIntendedOn;
    });
  }

  private stopMediaStreams(): void {
    this.detachVideoTrackWatch();
    if (this.userVideoStream) {
      this.userVideoStream.getTracks().forEach((track) => track.stop());
      this.userVideoStream = null;
    }

    if (this.demoMicStream) {
      this.demoMicStream.getTracks().forEach((track) => track.stop());
      this.demoMicStream = null;
    }

    if (
      this.avatarMediaStream &&
      this.avatarMediaStream !== this.userVideoStream
    ) {
      this.avatarMediaStream.getTracks().forEach((track) => track.stop());
      this.avatarMediaStream = null;
    }
  }
}
